// Google Analytics 4 — the single entry point for every gtag call in the app.
//
// Components never touch `window.gtag` directly: everything goes through the
// named helpers below, so the payload for each event is defined in exactly one
// place and can be audited here rather than hunted for across the codebase.
//
// PRIVACY: no email, name, phone or address is ever sent. What GA4 receives is
// the internal user id (an opaque UUID that means nothing outside our own
// database), cluster labels, prices, item names, coupon codes and step numbers.
// This is deliberately a wider payload than the Meta Pixel gets (see
// utils/metaPixel) — GA4 is our own product analytics, Meta is an ad network.
//
// The tag itself is bootstrapped in public/index.html, which only loads it when
// REACT_APP_GA4_MEASUREMENT_ID was set at build time. Every helper here is a
// no-op otherwise, so an unconfigured build (or a blocked script, or no
// network) changes nothing about how the site behaves.

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void
    dataLayer: unknown[]
  }
}

const GA_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID || ''

// Settings that must survive every `config` call, not just the first one.
//
// gtag treats each `config` as a fresh set of options for that tag: anything
// omitted falls back to the gtag default, NOT to what the previous call said.
// `send_page_view` defaults to TRUE, so a bare re-config (e.g. to attach the
// user id after login) would quietly fire a second, untitled page_view on top
// of the one GAPageTracker sends. Spreading this into every config call is what
// stops that.
const BASE_CONFIG = {
  send_page_view: false,
  cookie_flags:   'SameSite=None;Secure',
  cookie_expires: 63072000,
} as const

// The one guard. Analytics is never allowed to throw into a render or a click
// handler, so an unconfigured build, an ad blocker or a CSP rejection all end
// up here as a silent return.
function gtag(...args: unknown[]) {
  if (typeof window === 'undefined') return
  if (!window.gtag) return
  if (!GA_ID) return
  window.gtag(...args)
}

// ── User identity ────────────────────────────────────────────────────────────

/**
 * Stitch this browser to a TPC account. Call after a successful login/signup;
 * App's GAPageTracker also calls it whenever auth state resolves, so a session
 * restored from the refresh cookie is identified too.
 *
 * `userId` is the internal user UUID and nothing else — never the email.
 */
export function setUserId(userId: string) {
  gtag('config', GA_ID, { ...BASE_CONFIG, user_id: userId })
  gtag('set', 'user_properties', { tpc_user_id: userId })
}

/** Call on logout. `null`, not `undefined` — an undefined key is dropped from
 *  the config object and would leave the previous id in place. */
export function clearUserId() {
  gtag('config', GA_ID, { ...BASE_CONFIG, user_id: null })
  gtag('set', 'user_properties', { tpc_user_id: null })
}

// ── Page views ───────────────────────────────────────────────────────────────

// index.html configures the tag with send_page_view: false, so every page view
// in the app comes from here — one per route change, with a real title.
export function trackPageView(path: string, title: string) {
  gtag('event', 'page_view', {
    page_location: window.location.href,
    page_path:     path,
    page_title:    title,
  })
}

// ── Formulation funnel ───────────────────────────────────────────────────────

/** The quiz UI was opened. */
export function trackQuizStart() {
  gtag('event', 'quiz_start', {
    event_category: 'formulation',
  })
}

/** One question answered and left behind. `stepNumber` is 0 for the "who is
 *  this for?" pre-step, then 1..10 for the personality questions. */
export function trackQuizStep(stepNumber: number, questionKey: string) {
  gtag('event', 'quiz_step', {
    event_category: 'formulation',
    step_number:    stepNumber,
    question_key:   questionKey,
  })
}

/** The engine returned a formula. Fired once per successful generation. */
export function trackFormulaGenerated(clusterLabel: string) {
  gtag('event', 'formula_generated', {
    event_category: 'formulation',
    cluster_label:  clusterLabel,
  })
}

/** The customer is actually looking at their formula (result screen painted). */
export function trackFormulaViewed(clusterLabel: string) {
  gtag('event', 'formula_viewed', {
    event_category: 'formulation',
    cluster_label:  clusterLabel,
  })
}

/** The engine did not return a formula — HTTP error, an SSE `error` frame, or
 *  an aborted stream. `reason` is a short machine string, never a raw server
 *  message (which could carry detail we do not want in an analytics payload).
 *
 *  Someone who reached the loader and errored is otherwise indistinguishable
 *  from someone who wandered off: quiz_step 10 with no formula_generated after
 *  it looks the same either way. This is what separates them.
 */
export function trackFormulaFailed(reason: string) {
  gtag('event', 'formula_failed', {
    event_category: 'formulation',
    reason,   // 'http_error' | 'stream_error' | 'timeout' | 'auth'
  })
}

// ── Commerce funnel ──────────────────────────────────────────────────────────

// GA4's standard ecommerce shape, so add_to_cart / begin_checkout / purchase
// light up the built-in funnel reports without any custom modelling.
export function trackAddToCart(
  itemName: string,
  price: number,
  itemType: 'formulation' | 'product'
) {
  gtag('event', 'add_to_cart', {
    currency: 'INR',
    value:    price,
    items: [{
      item_name:     itemName,
      item_category: itemType,
      price:         price,
      quantity:      1,
    }]
  })
}

export function trackBeginCheckout(value: number) {
  gtag('event', 'begin_checkout', {
    currency: 'INR',
    value:    value,
  })
}

/** Store credits switched on at checkout. */
export function trackCreditsApplied(creditsAmount: number) {
  gtag('event', 'credits_applied', {
    event_category: 'checkout',
    credits_amount: creditsAmount,
  })
}

/** A coupon the server accepted for this cart. */
export function trackCouponApplied(couponCode: string, discountAmount: number) {
  gtag('event', 'coupon_applied', {
    event_category: 'checkout',
    coupon:         couponCode,
    discount:       discountAmount,
  })
}

/** The Cashfree sheet is about to open — the last step before money moves. */
export function trackPaymentInitiated(value: number, method: string) {
  gtag('event', 'payment_initiated', {
    event_category: 'checkout',
    currency:       'INR',
    value:          value,
    payment_method: method,
  })
}

/**
 * The complete vocabulary of `payment_failed`, as a type rather than a comment
 * so it can't be widened by hand at a call site:
 *
 *   'declined' — the gateway gave a verdict and it was no.
 *   'dropped'  — the customer closed the sheet without paying.
 *   'error'    — the attempt broke: SDK fault, dead gateway, thrown request.
 *
 * There is deliberately no value for "we stopped waiting". payment_failed means
 * CONFIRMED not paid, and a payment still in flight is not that — see the poll
 * timeout in pages/PaymentStatus.tsx, which fires nothing on purpose.
 */
export type PaymentFailReason = 'declined' | 'dropped' | 'error'

/** Payment did not complete after the sheet opened. The gap between
 *  payment_initiated and purchase is our most valuable drop-off; this names its
 *  cause. No order id, no amount beyond `value`, nothing from the gateway
 *  response — the gateway's own message is mapped to one of the fixed strings
 *  above by the caller and then discarded.
 */
export function trackPaymentFailed(value: number, reason: PaymentFailReason) {
  gtag('event', 'payment_failed', {
    event_category: 'checkout',
    currency:       'INR',
    value,
    reason,
  })
}

export function trackPurchase(
  orderId: string,
  value: number,
  coupon?: string,
  creditsUsed?: number
) {
  gtag('event', 'purchase', {
    transaction_id: orderId,
    currency:       'INR',
    value:          value,
    coupon:         coupon || '',
    items: [{
      item_name:     'TPC Custom Perfume',
      item_category: 'formulation',
      price:         value,
      quantity:      1,
    }]
  })
  // Credit spend is its own signal — how much of the revenue was funded by
  // credit rather than new money, which `value` alone can't tell us.
  if (creditsUsed && creditsUsed > 0) {
    gtag('event', 'credits_redeemed', {
      event_category: 'purchase',
      credits_amount: creditsUsed,
      order_id:       orderId,
    })
  }
}

/**
 * Claims the right to fire `purchase` for this order, exactly like
 * metaPixel.claimPurchaseTracking does for its own event — but on a SEPARATE
 * key, because the two tags are independent and one having counted an order
 * must not stop the other from counting it.
 *
 * Two screens can reach a completed payment (checkout's modal success and the
 * /payment/status return), and the status page is commonly reloaded while a UPI
 * collect settles. Returns true on the first claim only.
 *
 * sessionStorage, not module state: the return from the payment provider can be
 * a fresh page load. Storage being unavailable (private mode) claims
 * successfully — a duplicate conversion beats a missed one.
 */
export function claimGaPurchase(orderId: string): boolean {
  const key = `tpc_ga4_purchase_${orderId}`
  try {
    if (sessionStorage.getItem(key)) return false
    sessionStorage.setItem(key, '1')
    return true
  } catch {
    return true
  }
}

/**
 * The same claim, for `payment_failed`, on its own key.
 *
 * A failure has the same two-screen problem a purchase does — checkout sees the
 * modal close, and /payment/status sees the verified status when the customer
 * comes back or simply reloads while a UPI collect settles — so without this a
 * single abandoned payment lands two or three times. A retry is a brand new
 * order id, so it claims freshly and is counted, which is what we want.
 *
 * Separate from the purchase key on purpose: an order can legitimately fail and
 * then succeed on a later attempt, and neither claim must silence the other.
 */
export function claimGaPaymentFailure(orderId: string): boolean {
  const key = `tpc_ga4_payfail_${orderId}`
  try {
    if (sessionStorage.getItem(key)) return false
    sessionStorage.setItem(key, '1')
    return true
  } catch {
    return true
  }
}

// ── Auth events ──────────────────────────────────────────────────────────────

/** `method` is the credential the account was created with — never the value
 *  of that credential (no email, no phone number, ever leaves in an event). */
export function trackSignup(method: 'email' | 'google' | 'sms_otp') {
  gtag('event', 'sign_up', { method })
}

export function trackLogin(method: 'email' | 'google' | 'sms_otp') {
  gtag('event', 'login', { method })
}

// ── Engagement events ────────────────────────────────────────────────────────

/** A landing-page call to action was clicked. `location` distinguishes the hero
 *  button from any lower repeat, so a scroll-depth problem is separable from a
 *  copy problem: nobody reaching the closing band is a different failure from
 *  everybody reaching it and nobody pressing it.
 *
 *  Fills the hole between page_view on '/' and quiz_start, which until now had
 *  nothing between them at all. */
export function trackCtaClick(location: string) {
  gtag('event', 'cta_click', {
    event_category: 'engagement',
    cta_location:   location,   // 'hero' | 'mid' | 'footer'
  })
}

/** Landing page scroll milestones. Fired at most once per threshold per page
 *  view — see the listener in pages/Landing.tsx, which owns the de-duplication
 *  and is deliberately the only caller. */
export function trackScrollDepth(percent: 25 | 50 | 75 | 100) {
  gtag('event', 'scroll_depth', {
    event_category: 'engagement',
    percent,
  })
}

/** Pre-launch waitlist joined. The name/phone/email typed into that form are
 *  deliberately not sent — only that a signup happened. */
export function trackWaitlistSignup() {
  gtag('event', 'waitlist_signup', {
    event_category: 'engagement',
  })
}

/** "Notify me" on an out-of-stock item. The WhatsApp number is never sent. */
export function trackNotifyMe(productName: string) {
  gtag('event', 'notify_me', {
    event_category: 'engagement',
    item_name:      productName,
  })
}

/** A coupon code copied to the clipboard from the offers strip. */
export function trackCouponCopied(couponCode: string) {
  gtag('event', 'coupon_copied', {
    event_category: 'engagement',
    coupon:         couponCode,
  })
}

/** Festival banner CTA. No festival banner ships today — this is here so the
 *  event is defined in one place when that feature lands. */
export function trackFestivalCTA(festivalName: string) {
  gtag('event', 'festival_cta_click', {
    event_category: 'festival',
    festival_name:  festivalName,
  })
}

/** A catalogue product detail page was opened. */
export function trackProductView(
  productName: string,
  productId: string,
  price: number
) {
  gtag('event', 'view_item', {
    currency: 'INR',
    value:    price,
    items: [{
      item_id:       productId,
      item_name:     productName,
      item_category: 'product',
      price:         price,
    }]
  })
}

/** Shop search. Fired on the settled term, not per keystroke. */
export function trackSearch(searchTerm: string) {
  gtag('event', 'search', { search_term: searchTerm })
}

/** Social sharing. No share affordance ships today — defined ahead of it, for
 *  the same reason as trackFestivalCTA. */
export function trackShare(method: string, contentType: string) {
  gtag('event', 'share', { method, content_type: contentType })
}

// ── UTM capture ──────────────────────────────────────────────────────────────

/**
 * Read the campaign params off the current URL and keep them for the rest of
 * the session. GA4 does its own attribution from the same params; this copy
 * exists so our OWN records (an order, a support conversation) can be
 * cross-referenced against a campaign without querying GA.
 *
 * sessionStorage, not localStorage: an attribution window that outlives the
 * browser tab would credit a campaign the visitor arrived from days ago.
 */
export function captureUTMParams(): Record<string, string> {
  const params = new URLSearchParams(window.location.search)
  const utms: Record<string, string> = {}
  const utmKeys = [
    'utm_source', 'utm_medium', 'utm_campaign',
    'utm_content', 'utm_term',
  ]
  utmKeys.forEach(key => {
    const val = params.get(key)
    if (val) utms[key] = val
  })
  // Only write when this URL actually carried campaign params — a later
  // in-app navigation has none, and must not erase the landing's.
  if (Object.keys(utms).length > 0) {
    try {
      sessionStorage.setItem('tpc_utms', JSON.stringify(utms))
    } catch { /* private mode — attribution is best-effort */ }
  }
  return utms
}

/** The UTMs this session landed with, for anything that wants to record them
 *  alongside our own data. `{}` when the visit carried none. */
export function getStoredUTMs(): Record<string, string> {
  try {
    return JSON.parse(sessionStorage.getItem('tpc_utms') || '{}')
  } catch {
    return {}
  }
}
